/**
 * User Management Component
 *
 * Provides a comprehensive interface for Tenant Admins to manage users:
 * - List users with search, filtering, and pagination
 * - Create new users
 * - Edit existing users
 * - Activate/deactivate users
 * - Delete users (with confirmation)
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth, type Role } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { getApiClient, isSuccess } from '../../api/client';
import { Layout } from '../layout/Layout';

// =============================================================================
// Types
// =============================================================================

/**
 * User with stats
 */
interface UserWithStats {
  id: string;
  email: string;
  name: string;
  role: Role;
  active: boolean;
  tenantId: string | null;
  lastLogin: string | null;
  createdAt: string;
  updatedAt: string;
  stats?: {
    botCount: number;
    activeBotCount: number;
  };
}

/**
 * API response for user list
 */
interface UsersResponse {
  users: UserWithStats[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasMore: boolean;
  };
}

/**
 * Delete preview response
 */
interface DeletePreviewResponse {
  id: string;
  name: string;
  resourceCounts: {
    botCount: number;
    auditLogCount: number;
  };
  warning: string;
}

/**
 * User form data
 */
interface UserFormData {
  id?: string;
  email: string;
  name: string;
  role: Role;
  active: boolean;
  password?: string;
  tenantId?: string | null;
}

/**
 * Tenant for dropdown
 */
interface TenantOption {
  id: string;
  name: string;
  slug: string;
}

/**
 * Form validation errors
 */
interface FormErrors {
  email?: string;
  name?: string;
  password?: string;
  role?: string;
  general?: string;
}

/**
 * Filter state
 */
type StatusFilter = 'all' | 'active' | 'inactive';
type RoleFilter = 'all' | 'TENANT_ADMIN' | 'TENANT_USER' | 'VIEWER';

/**
 * Modal state
 */
type ModalState =
  | { type: 'none' }
  | { type: 'create' }
  | { type: 'edit'; user: UserWithStats }
  | { type: 'delete'; user: UserWithStats; preview?: DeletePreviewResponse };

// =============================================================================
// Constants
// =============================================================================

const STATUS_COLORS = {
  active: { bg: 'bg-green-900/20', text: 'text-green-400', dot: 'bg-green-400' },
  inactive: { bg: 'bg-gray-900/20', text: 'text-gray-400', dot: 'bg-gray-400' },
} as const;

const ROLE_COLORS: Record<Role, { bg: string; text: string }> = {
  GLOBAL_ADMIN: { bg: 'bg-purple-900/20', text: 'text-purple-400' },
  TENANT_ADMIN: { bg: 'bg-blue-900/20', text: 'text-blue-400' },
  TENANT_USER: { bg: 'bg-cyan-900/20', text: 'text-cyan-400' },
  VIEWER: { bg: 'bg-gray-700/20', text: 'text-gray-400' },
};

const ROLE_LABELS: Record<Role, string> = {
  GLOBAL_ADMIN: 'Global Admin',
  TENANT_ADMIN: 'Tenant Admin',
  TENANT_USER: 'Tenant User',
  VIEWER: 'Viewer',
};

// Available roles for tenant admins to assign
const TENANT_ADMIN_ROLES: Role[] = ['TENANT_ADMIN', 'TENANT_USER', 'VIEWER'];

// Available roles for global admins to assign (includes GLOBAL_ADMIN)
const GLOBAL_ADMIN_ROLES: Role[] = ['GLOBAL_ADMIN', 'TENANT_ADMIN', 'TENANT_USER', 'VIEWER'];

// =============================================================================
// Sub-components
// =============================================================================

/**
 * Status badge component
 */
function StatusBadge({ active }: { active: boolean }) {
  const colors = active ? STATUS_COLORS.active : STATUS_COLORS.inactive;

  return (
    <div
      className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${colors.bg} ${colors.text}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${colors.dot}`} />
      {active ? 'Active' : 'Inactive'}
    </div>
  );
}

/**
 * Role badge component
 */
function RoleBadge({ role }: { role: Role }) {
  const colors = ROLE_COLORS[role];

  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${colors.bg} ${colors.text}`}
    >
      {ROLE_LABELS[role]}
    </span>
  );
}

/**
 * User card component
 */
interface UserCardProps {
  user: UserWithStats;
  onEdit: (user: UserWithStats) => void;
  onToggleActive: (user: UserWithStats) => void;
  onDelete: (user: UserWithStats) => void;
  isLoading: boolean;
  currentUserId: string | null;
}

function UserCard({
  user,
  onEdit,
  onToggleActive,
  onDelete,
  isLoading,
  currentUserId,
}: UserCardProps) {
  const isSelf = user.id === currentUserId;

  return (
    <div className="rounded-lg bg-discord-darker border border-discord-light p-4 transition-all hover:border-discord-blurple hover:shadow-lg">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <button
              onClick={() => onEdit(user)}
              className="font-semibold text-white hover:text-discord-blurple transition-colors truncate block text-left"
            >
              {user.name}
            </button>
            {isSelf && (
              <span className="text-xs text-discord-blurple">(You)</span>
            )}
          </div>
          <p className="mt-1 text-sm text-gray-400 truncate">{user.email}</p>
        </div>
        <StatusBadge active={user.active} />
      </div>

      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <RoleBadge role={user.role} />
        {user.stats && user.stats.botCount > 0 && (
          <span className="text-xs text-gray-500">
            {user.stats.botCount} bots
            {user.stats.activeBotCount > 0 && (
              <span className="text-green-400 ml-1">({user.stats.activeBotCount} running)</span>
            )}
          </span>
        )}
      </div>

      <div className="mt-3 flex items-center justify-between">
        <span className="text-xs text-gray-500">
          {user.lastLogin
            ? `Last login: ${new Date(user.lastLogin).toLocaleDateString()}`
            : 'Never logged in'}
        </span>

        <div className="flex items-center gap-2">
          <button
            onClick={() => onToggleActive(user)}
            disabled={isLoading || isSelf}
            className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              user.active
                ? 'bg-yellow-600/20 text-yellow-400 hover:bg-yellow-600/30'
                : 'bg-green-600/20 text-green-400 hover:bg-green-600/30'
            }`}
            title={isSelf ? 'Cannot change your own status' : user.active ? 'Deactivate user' : 'Activate user'}
          >
            {user.active ? 'Deactivate' : 'Activate'}
          </button>
          <button
            onClick={() => onEdit(user)}
            disabled={isLoading}
            className="rounded-md bg-discord-light px-2.5 py-1 text-xs font-medium text-gray-300 hover:bg-discord-blurple hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Edit user"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
          </button>
          <button
            onClick={() => onDelete(user)}
            disabled={isLoading || isSelf}
            className="rounded-md bg-red-600/20 px-2.5 py-1 text-xs font-medium text-red-400 hover:bg-red-600/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title={isSelf ? 'Cannot delete yourself' : 'Delete user'}
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * User row component for table view
 */
interface UserRowProps {
  user: UserWithStats;
  onEdit: (user: UserWithStats) => void;
  onToggleActive: (user: UserWithStats) => void;
  onDelete: (user: UserWithStats) => void;
  isLoading: boolean;
  currentUserId: string | null;
}

function UserRow({
  user,
  onEdit,
  onToggleActive,
  onDelete,
  isLoading,
  currentUserId,
}: UserRowProps) {
  const isSelf = user.id === currentUserId;

  return (
    <tr className="border-b border-discord-light hover:bg-discord-light/30 transition-colors">
      <td className="px-4 py-3">
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <button
              onClick={() => onEdit(user)}
              className="font-medium text-white hover:text-discord-blurple transition-colors text-left"
            >
              {user.name}
            </button>
            {isSelf && (
              <span className="text-xs text-discord-blurple">(You)</span>
            )}
          </div>
          <span className="text-sm text-gray-500">{user.email}</span>
        </div>
      </td>
      <td className="px-4 py-3">
        <RoleBadge role={user.role} />
      </td>
      <td className="px-4 py-3">
        <StatusBadge active={user.active} />
      </td>
      <td className="px-4 py-3 text-sm text-gray-400">
        {user.stats?.botCount ?? 0}
        {user.stats?.activeBotCount ? (
          <span className="text-green-400 ml-1">({user.stats.activeBotCount} running)</span>
        ) : null}
      </td>
      <td className="px-4 py-3 text-sm text-gray-400">
        {user.lastLogin
          ? new Date(user.lastLogin).toLocaleDateString()
          : 'Never'}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <button
            onClick={() => onToggleActive(user)}
            disabled={isLoading || isSelf}
            className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              user.active
                ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
            title={isSelf ? 'Cannot change your own status' : undefined}
          >
            {user.active ? 'Deactivate' : 'Activate'}
          </button>
          <button
            onClick={() => onEdit(user)}
            disabled={isLoading}
            className="rounded-md bg-discord-light px-3 py-1.5 text-xs font-medium text-gray-300 hover:bg-discord-blurple hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Edit
          </button>
          <button
            onClick={() => onDelete(user)}
            disabled={isLoading || isSelf}
            className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title={isSelf ? 'Cannot delete yourself' : undefined}
          >
            Delete
          </button>
        </div>
      </td>
    </tr>
  );
}

/**
 * Status filter buttons component
 */
interface StatusFilterProps {
  value: StatusFilter;
  onChange: (filter: StatusFilter) => void;
  counts: Record<StatusFilter, number>;
}

function StatusFilterButtons({ value, onChange, counts }: StatusFilterProps) {
  const filters: { key: StatusFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'active', label: 'Active' },
    { key: 'inactive', label: 'Inactive' },
  ];

  return (
    <div className="flex items-center gap-2">
      {filters.map(({ key, label }) => (
        <button
          key={key}
          onClick={() => onChange(key)}
          className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
            value === key
              ? 'bg-discord-blurple text-white'
              : 'bg-discord-light text-gray-300 hover:text-white'
          }`}
        >
          {label}
          {counts[key] > 0 && (
            <span
              className={`ml-1.5 rounded-full px-1.5 py-0.5 text-xs ${
                value === key ? 'bg-white/20' : 'bg-discord-darker'
              }`}
            >
              {counts[key]}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

/**
 * Role filter dropdown component
 */
interface RoleFilterProps {
  value: RoleFilter;
  onChange: (filter: RoleFilter) => void;
}

function RoleFilterDropdown({ value, onChange }: RoleFilterProps) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as RoleFilter)}
      className="rounded-md border border-discord-light bg-discord-darker py-2 px-3 text-sm text-white focus:border-discord-blurple focus:outline-none focus:ring-1 focus:ring-discord-blurple"
    >
      <option value="all">All Roles</option>
      <option value="TENANT_ADMIN">Tenant Admin</option>
      <option value="TENANT_USER">Tenant User</option>
      <option value="VIEWER">Viewer</option>
    </select>
  );
}

/**
 * Empty state component
 */
function EmptyState({
  filter,
  onCreateClick,
}: {
  filter: StatusFilter;
  onCreateClick: () => void;
}) {
  const messages: Record<StatusFilter, { title: string; description: string }> = {
    all: {
      title: 'No users yet',
      description: 'Create your first user to get started',
    },
    active: {
      title: 'No active users',
      description: 'All users are currently inactive',
    },
    inactive: {
      title: 'No inactive users',
      description: 'All users are currently active',
    },
  };

  const { title, description } = messages[filter];

  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <svg
        className="h-12 w-12 text-gray-500 mb-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
        />
      </svg>
      <h3 className="text-lg font-medium text-white">{title}</h3>
      <p className="mt-1 text-sm text-gray-400">{description}</p>
      {filter === 'all' && (
        <button
          onClick={onCreateClick}
          className="mt-4 inline-flex items-center rounded-md bg-discord-blurple px-4 py-2 text-sm font-medium text-white hover:bg-discord-blurple/90 transition-colors"
        >
          Create User
        </button>
      )}
    </div>
  );
}

/**
 * Loading skeleton component
 */
function LoadingSkeleton() {
  return (
    <div className="animate-pulse space-y-4">
      <div className="flex items-center justify-between">
        <div className="h-8 w-48 bg-discord-light rounded" />
        <div className="h-8 w-32 bg-discord-light rounded" />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="rounded-lg bg-discord-darker border border-discord-light p-4">
            <div className="flex items-start justify-between">
              <div className="space-y-2">
                <div className="h-5 w-32 bg-discord-light rounded" />
                <div className="h-4 w-48 bg-discord-light rounded" />
              </div>
              <div className="h-6 w-16 bg-discord-light rounded-full" />
            </div>
            <div className="mt-4 flex items-center justify-between">
              <div className="h-4 w-24 bg-discord-light rounded" />
              <div className="flex gap-2">
                <div className="h-8 w-16 bg-discord-light rounded" />
                <div className="h-8 w-8 bg-discord-light rounded" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Modal component
 */
function Modal({
  isOpen,
  onClose,
  title,
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black/60 transition-opacity"
          onClick={onClose}
        />
        <div className="relative w-full max-w-lg rounded-lg bg-discord-dark border border-discord-light p-6 shadow-xl">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-white">{title}</h2>
            <button
              onClick={onClose}
              className="rounded-md p-1 text-gray-400 hover:text-white transition-colors"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

/**
 * User form component
 */
interface UserFormProps {
  user: UserWithStats | null;
  onSubmit: (user: UserFormData) => void;
  onCancel: () => void;
  isLoading: boolean;
  currentUserId: string | null;
  isGlobalAdmin: boolean;
  effectiveTenantId: string | null;
}

function UserForm({
  user,
  onSubmit,
  onCancel,
  isLoading,
  currentUserId,
  isGlobalAdmin,
  effectiveTenantId,
}: UserFormProps) {
  const isEditMode = !!user?.id;
  const isSelf = user?.id === currentUserId;

  const [email, setEmail] = useState(user?.email || '');
  const [name, setName] = useState(user?.name || '');
  const [role, setRole] = useState<Role>(user?.role || 'TENANT_USER');
  const [active, setActive] = useState(user?.active ?? true);
  const [password, setPassword] = useState('');
  const [tenantId, setTenantId] = useState<string | null>(user?.tenantId ?? effectiveTenantId);
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [isLoadingTenants, setIsLoadingTenants] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});

  // Get assignable roles based on current user's role
  const assignableRoles = isGlobalAdmin ? GLOBAL_ADMIN_ROLES : TENANT_ADMIN_ROLES;

  // Fetch tenants for global admins
  useEffect(() => {
    if (!isGlobalAdmin) return;

    const fetchTenants = async () => {
      setIsLoadingTenants(true);
      const apiClient = getApiClient();
      try {
        const result = await apiClient.get<{ tenants: TenantOption[] }>('/tenants?limit=100');
        if (isSuccess(result)) {
          setTenants(result.data.tenants);
        }
      } catch {
        // Ignore errors - tenant list is optional
      } finally {
        setIsLoadingTenants(false);
      }
    };

    void fetchTenants();
  }, [isGlobalAdmin]);

  // When role changes to GLOBAL_ADMIN, clear tenant
  useEffect(() => {
    if (role === 'GLOBAL_ADMIN') {
      setTenantId(null);
    }
  }, [role]);

  const validate = useCallback((): boolean => {
    const newErrors: FormErrors = {};

    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!name.trim()) {
      newErrors.name = 'Name is required';
    } else if (name.length > 100) {
      newErrors.name = 'Name must be at most 100 characters';
    }

    if (!isEditMode && !password) {
      newErrors.password = 'Password is required for new users';
    } else if (password && password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (!assignableRoles.includes(role)) {
      newErrors.role = 'Invalid role selected';
    }

    // Non-global-admin users must have a tenant
    if (role !== 'GLOBAL_ADMIN' && !tenantId && isGlobalAdmin) {
      newErrors.general = 'Please select a tenant for non-global admin users';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [email, name, password, role, isEditMode, assignableRoles, tenantId, isGlobalAdmin]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!validate()) return;

      setIsSubmitting(true);
      setErrors({});

      const apiClient = getApiClient();

      try {
        const payload: UserFormData = {
          email: email.trim(),
          name: name.trim(),
          role,
          active,
          tenantId: role === 'GLOBAL_ADMIN' ? null : tenantId,
        };

        if (password) {
          payload.password = password;
        }

        let result;
        if (isEditMode) {
          payload.id = user?.id;
          result = await apiClient.put<UserFormData>(`/users/${user?.id}`, payload);
        } else {
          result = await apiClient.post<UserFormData>('/users', payload);
        }

        if (isSuccess(result)) {
          onSubmit(result.data);
        } else {
          setErrors({ general: result.error.message || 'Failed to save user' });
        }
      } catch {
        setErrors({ general: 'Failed to save user. Please try again.' });
      } finally {
        setIsSubmitting(false);
      }
    },
    [validate, email, name, role, active, password, tenantId, isEditMode, user?.id, onSubmit]
  );

  const inputClasses = (hasError: boolean) =>
    `w-full rounded-md border bg-discord-darkest py-2 px-3 text-white placeholder-gray-500 focus:outline-none focus:ring-1 ${
      hasError
        ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
        : 'border-discord-light focus:border-discord-blurple focus:ring-discord-blurple'
    } ${isLoading || isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {errors.general && (
        <div className="rounded-md bg-red-900/50 border border-red-500 p-4">
          <div className="flex items-start">
            <svg
              className="mr-3 h-5 w-5 flex-shrink-0 text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div className="flex-1">
              <p className="text-sm text-red-200">{errors.general}</p>
            </div>
            <button
              type="button"
              onClick={() => setErrors((prev) => ({ ...prev, general: undefined }))}
              className="ml-3 text-sm font-medium text-red-400 hover:text-red-300"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      <div className="rounded-lg bg-discord-darker border border-discord-light p-6 space-y-4">
        {/* Name */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-1.5">
            Full Name
            <span className="text-red-400 ml-1">*</span>
          </label>
          <input
            type="text"
            id="name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setErrors((prev) => ({ ...prev, name: undefined }));
            }}
            placeholder="John Doe"
            disabled={isLoading || isSubmitting}
            className={inputClasses(!!errors.name)}
          />
          {errors.name && <p className="mt-1 text-xs text-red-400">{errors.name}</p>}
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1.5">
            Email Address
            <span className="text-red-400 ml-1">*</span>
          </label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setErrors((prev) => ({ ...prev, email: undefined }));
            }}
            placeholder="user@example.com"
            disabled={isLoading || isSubmitting}
            className={inputClasses(!!errors.email)}
          />
          {errors.email && <p className="mt-1 text-xs text-red-400">{errors.email}</p>}
        </div>

        {/* Password */}
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-1.5">
            Password
            {!isEditMode && <span className="text-red-400 ml-1">*</span>}
          </label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setErrors((prev) => ({ ...prev, password: undefined }));
            }}
            placeholder={isEditMode ? 'Leave blank to keep current' : 'Enter password'}
            disabled={isLoading || isSubmitting}
            className={inputClasses(!!errors.password)}
          />
          {!errors.password && (
            <p className="mt-1 text-xs text-gray-500">
              {isEditMode
                ? 'Leave blank to keep the current password'
                : 'Must be at least 8 characters'}
            </p>
          )}
          {errors.password && <p className="mt-1 text-xs text-red-400">{errors.password}</p>}
        </div>

        {/* Role */}
        <div>
          <label htmlFor="role" className="block text-sm font-medium text-gray-300 mb-1.5">
            Role
            <span className="text-red-400 ml-1">*</span>
          </label>
          <select
            id="role"
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
            disabled={isLoading || isSubmitting || isSelf}
            className={`${inputClasses(!!errors.role)} ${isSelf ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {assignableRoles.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABELS[r]}
              </option>
            ))}
          </select>
          {isSelf && (
            <p className="mt-1 text-xs text-yellow-400">You cannot change your own role</p>
          )}
          {errors.role && <p className="mt-1 text-xs text-red-400">{errors.role}</p>}
          {role === 'GLOBAL_ADMIN' && (
            <p className="mt-1 text-xs text-purple-400">Global Admins have access to all tenants</p>
          )}
        </div>

        {/* Tenant (for Global Admins only) */}
        {isGlobalAdmin && role !== 'GLOBAL_ADMIN' && (
          <div>
            <label htmlFor="tenantId" className="block text-sm font-medium text-gray-300 mb-1.5">
              Tenant
              <span className="text-red-400 ml-1">*</span>
            </label>
            <select
              id="tenantId"
              value={tenantId || ''}
              onChange={(e) => setTenantId(e.target.value || null)}
              disabled={isLoading || isSubmitting || isLoadingTenants}
              className={inputClasses(false)}
            >
              <option value="">Select a tenant...</option>
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.slug})
                </option>
              ))}
            </select>
            {isLoadingTenants && (
              <p className="mt-1 text-xs text-gray-500">Loading tenants...</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              The tenant this user will belong to
            </p>
          </div>
        )}

        {/* Active Toggle */}
        <div className="flex items-center justify-between">
          <div>
            <span className="text-sm font-medium text-gray-300">Active</span>
            <p className="text-xs text-gray-500">Inactive users cannot log in</p>
          </div>
          <button
            type="button"
            onClick={() => !isSelf && setActive(!active)}
            disabled={isLoading || isSubmitting || isSelf}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              active ? 'bg-discord-blurple' : 'bg-discord-light'
            } ${isSelf ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                active ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
        {isSelf && (
          <p className="text-xs text-yellow-400">You cannot change your own status</p>
        )}
      </div>

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="rounded-md bg-discord-light px-4 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading || isSubmitting}
          className="rounded-md bg-discord-blurple px-4 py-2 text-sm font-medium text-white hover:bg-discord-blurple/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting
            ? isEditMode
              ? 'Saving...'
              : 'Creating...'
            : isEditMode
              ? 'Save Changes'
              : 'Create User'}
        </button>
      </div>
    </form>
  );
}

/**
 * Delete confirmation modal content
 */
function DeleteConfirmation({
  user,
  preview,
  onConfirm,
  onCancel,
  isLoading,
}: {
  user: UserWithStats;
  preview?: DeletePreviewResponse;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading: boolean;
}) {
  return (
    <div className="space-y-4">
      <div className="rounded-md bg-red-900/30 border border-red-500/50 p-4">
        <div className="flex items-start">
          <svg
            className="mr-3 h-5 w-5 flex-shrink-0 text-red-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <div>
            <h4 className="text-sm font-medium text-red-300">
              Permanently delete user?
            </h4>
            <p className="mt-1 text-sm text-red-200/80">
              This action cannot be undone. All data associated with this user will be
              permanently deleted.
            </p>
          </div>
        </div>
      </div>

      <div className="text-sm text-gray-300">
        <p>
          You are about to delete <strong className="text-white">{user.name}</strong>
          <span className="text-gray-500 ml-1">({user.email})</span>
        </p>
      </div>

      {preview && (
        <div className="rounded-md bg-discord-darker border border-discord-light p-4">
          <h4 className="text-sm font-medium text-gray-300 mb-2">Resources to be deleted:</h4>
          <ul className="text-sm text-gray-400 space-y-1">
            <li className="flex items-center gap-2">
              <span className="font-mono text-red-400">{preview.resourceCounts.botCount}</span>
              <span>bots created by this user</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="font-mono text-red-400">{preview.resourceCounts.auditLogCount}</span>
              <span>audit log entries</span>
            </li>
          </ul>
        </div>
      )}

      <div className="flex justify-end gap-3 pt-4">
        <button
          onClick={onCancel}
          disabled={isLoading}
          className="rounded-md bg-discord-light px-4 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={isLoading}
          className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Deleting...' : 'Delete User'}
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

/**
 * User management page component
 *
 * Provides comprehensive user management for Tenant Admins:
 * - List users with filtering and search
 * - Create, edit, activate/deactivate, and delete users
 */
export function UserManagement() {
  const { user: authUser } = useAuth();
  const { isTenantAdmin, isGlobalAdmin, effectiveTenantId } = useTenant();

  const [users, setUsers] = useState<UserWithStats[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [modal, setModal] = useState<ModalState>({ type: 'none' });
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
    hasMore: false,
  });

  /**
   * Fetch users from API
   */
  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const apiClient = getApiClient();

    try {
      const queryParams = new URLSearchParams({
        page: String(page),
        limit: '20',
        sortBy: 'createdAt',
        sortOrder: 'desc',
      });

      if (searchQuery) {
        queryParams.set('search', searchQuery);
      }

      if (statusFilter !== 'all') {
        queryParams.set('active', statusFilter === 'active' ? 'true' : 'false');
      }

      if (roleFilter !== 'all') {
        queryParams.set('role', roleFilter);
      }

      if (effectiveTenantId) {
        queryParams.set('tenantId', effectiveTenantId);
      }

      const result = await apiClient.get<UsersResponse>(`/users?${queryParams}`);

      if (isSuccess(result)) {
        setUsers(result.data.users);
        setPagination({
          total: result.data.pagination.total,
          totalPages: result.data.pagination.totalPages,
          hasMore: result.data.pagination.hasMore,
        });
      } else {
        setError(result.error.message || 'Failed to fetch users');
      }
    } catch {
      setError('Failed to load users. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [page, searchQuery, statusFilter, roleFilter, effectiveTenantId]);

  /**
   * Toggle user active status
   */
  const handleToggleActive = useCallback(async (user: UserWithStats) => {
    if (user.id === authUser?.id) return;

    setIsActionLoading(true);
    setError(null);

    const apiClient = getApiClient();
    const endpoint = user.active
      ? `/users/${user.id}/deactivate`
      : `/users/${user.id}/reactivate`;

    try {
      const result = await apiClient.post<UserWithStats>(endpoint);

      if (isSuccess(result)) {
        setUsers((prev) =>
          prev.map((u) =>
            u.id === user.id ? { ...u, active: !user.active } : u
          )
        );
      } else {
        setError(result.error.message || 'Failed to update user status');
      }
    } catch {
      setError('Failed to update user. Please try again.');
    } finally {
      setIsActionLoading(false);
    }
  }, [authUser?.id]);

  /**
   * Open delete confirmation modal
   */
  const handleDeleteClick = useCallback(async (user: UserWithStats) => {
    if (user.id === authUser?.id) return;

    setModal({ type: 'delete', user });

    const apiClient = getApiClient();
    try {
      const result = await apiClient.get<DeletePreviewResponse>(
        `/users/${user.id}/preview-delete`
      );

      if (isSuccess(result)) {
        setModal({ type: 'delete', user, preview: result.data });
      }
    } catch {
      // Continue without preview
    }
  }, [authUser?.id]);

  /**
   * Delete user
   */
  const handleDeleteConfirm = useCallback(async () => {
    if (modal.type !== 'delete') return;

    setIsActionLoading(true);
    setError(null);

    const apiClient = getApiClient();

    try {
      const result = await apiClient.delete<{ id: string; deleted: boolean }>(
        `/users/${modal.user.id}`
      );

      if (isSuccess(result)) {
        setUsers((prev) => prev.filter((u) => u.id !== modal.user.id));
        setModal({ type: 'none' });
      } else {
        setError(result.error.message || 'Failed to delete user');
      }
    } catch {
      setError('Failed to delete user. Please try again.');
    } finally {
      setIsActionLoading(false);
    }
  }, [modal]);

  /**
   * Handle form submission (create/edit)
   */
  const handleFormSubmit = useCallback(
    (data: UserFormData) => {
      if (modal.type === 'create') {
        const newUser: UserWithStats = {
          id: data.id!,
          email: data.email,
          name: data.name,
          role: data.role,
          active: data.active,
          tenantId: effectiveTenantId,
          lastLogin: null,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        setUsers((prev) => [newUser, ...prev]);
      } else if (modal.type === 'edit') {
        setUsers((prev) =>
          prev.map((u) =>
            u.id === data.id
              ? {
                  ...u,
                  ...data,
                  updatedAt: new Date().toISOString(),
                }
              : u
          )
        );
      }
      setModal({ type: 'none' });
    },
    [modal, effectiveTenantId]
  );

  /**
   * Initial data fetch
   */
  useEffect(() => {
    void fetchUsers();
  }, [fetchUsers]);

  /**
   * Calculate status counts
   */
  const statusCounts = useMemo(() => {
    return {
      all: users.length,
      active: users.filter((u) => u.active).length,
      inactive: users.filter((u) => !u.active).length,
    };
  }, [users]);

  /**
   * Filter users by search (client-side for current page)
   */
  const filteredUsers = useMemo(() => {
    let result = users;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (user) =>
          user.name.toLowerCase().includes(query) ||
          user.email.toLowerCase().includes(query)
      );
    }

    return result;
  }, [users, searchQuery]);

  // Access control check
  if (!isTenantAdmin && !isGlobalAdmin) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <svg
            className="h-12 w-12 text-red-400 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <h3 className="text-lg font-medium text-white">Access Denied</h3>
          <p className="mt-1 text-sm text-gray-400">
            You must be a Tenant Admin or Global Admin to access user management.
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">User Management</h1>
            <p className="mt-1 text-gray-400">
              Manage users and their access to the platform
            </p>
          </div>
          <button
            onClick={() => setModal({ type: 'create' })}
            className="inline-flex items-center gap-2 rounded-md bg-discord-blurple px-4 py-2 text-sm font-medium text-white hover:bg-discord-blurple/90 transition-colors"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New User
          </button>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="rounded-md bg-red-900/50 border border-red-500 p-4">
            <div className="flex items-start">
              <svg
                className="mr-3 h-5 w-5 flex-shrink-0 text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="flex-1">
                <p className="text-sm text-red-200">{error}</p>
              </div>
              <button
                onClick={() => setError(null)}
                className="ml-3 text-sm font-medium text-red-400 hover:text-red-300"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {isLoading ? (
          <LoadingSkeleton />
        ) : (
          <>
            {/* Filters and Controls */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <StatusFilterButtons
                value={statusFilter}
                onChange={setStatusFilter}
                counts={statusCounts}
              />

              <div className="flex items-center gap-3">
                {/* Role Filter */}
                <RoleFilterDropdown value={roleFilter} onChange={setRoleFilter} />

                {/* Search Input */}
                <div className="relative">
                  <svg
                    className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-48 rounded-md border border-discord-light bg-discord-darker py-2 pl-10 pr-4 text-sm text-white placeholder-gray-500 focus:border-discord-blurple focus:outline-none focus:ring-1 focus:ring-discord-blurple"
                  />
                </div>

                {/* View Toggle */}
                <div className="flex rounded-md bg-discord-light">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`rounded-l-md px-3 py-2 text-sm transition-colors ${
                      viewMode === 'grid'
                        ? 'bg-discord-blurple text-white'
                        : 'text-gray-400 hover:text-white'
                    }`}
                    title="Grid view"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={() => setViewMode('table')}
                    className={`rounded-r-md px-3 py-2 text-sm transition-colors ${
                      viewMode === 'table'
                        ? 'bg-discord-blurple text-white'
                        : 'text-gray-400 hover:text-white'
                    }`}
                    title="Table view"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 6h16M4 10h16M4 14h16M4 18h16"
                      />
                    </svg>
                  </button>
                </div>

                {/* Refresh Button */}
                <button
                  onClick={() => void fetchUsers()}
                  disabled={isLoading}
                  className="rounded-md bg-discord-light p-2 text-gray-400 hover:text-white transition-colors disabled:opacity-50"
                  title="Refresh"
                >
                  <svg
                    className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* User List */}
            {filteredUsers.length === 0 ? (
              <EmptyState
                filter={statusFilter}
                onCreateClick={() => setModal({ type: 'create' })}
              />
            ) : viewMode === 'grid' ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {filteredUsers.map((user) => (
                  <UserCard
                    key={user.id}
                    user={user}
                    onEdit={(u) => setModal({ type: 'edit', user: u })}
                    onToggleActive={handleToggleActive}
                    onDelete={handleDeleteClick}
                    isLoading={isActionLoading}
                    currentUserId={authUser?.id ?? null}
                  />
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-discord-light overflow-hidden">
                <table className="w-full">
                  <thead className="bg-discord-darker">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                        User
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                        Role
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                        Bots
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                        Last Login
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-discord-dark">
                    {filteredUsers.map((user) => (
                      <UserRow
                        key={user.id}
                        user={user}
                        onEdit={(u) => setModal({ type: 'edit', user: u })}
                        onToggleActive={handleToggleActive}
                        onDelete={handleDeleteClick}
                        isLoading={isActionLoading}
                        currentUserId={authUser?.id ?? null}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-400">
                  Page {page} of {pagination.totalPages} ({pagination.total} users)
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="rounded-md bg-discord-light px-3 py-1.5 text-sm font-medium text-gray-300 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={!pagination.hasMore}
                    className="rounded-md bg-discord-light px-3 py-1.5 text-sm font-medium text-gray-300 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}

            {/* Summary */}
            <div className="flex items-center justify-between text-sm text-gray-400">
              <span>
                Showing {filteredUsers.length} of {users.length} users
              </span>
              <span>
                {statusCounts.active} active · {statusCounts.inactive} inactive
              </span>
            </div>
          </>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={modal.type === 'create' || modal.type === 'edit'}
        onClose={() => setModal({ type: 'none' })}
        title={modal.type === 'create' ? 'Create New User' : 'Edit User'}
      >
        <UserForm
          user={modal.type === 'edit' ? modal.user : null}
          onSubmit={handleFormSubmit}
          onCancel={() => setModal({ type: 'none' })}
          isLoading={isActionLoading}
          currentUserId={authUser?.id ?? null}
          isGlobalAdmin={isGlobalAdmin}
          effectiveTenantId={effectiveTenantId}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={modal.type === 'delete'}
        onClose={() => setModal({ type: 'none' })}
        title="Delete User"
      >
        {modal.type === 'delete' && (
          <DeleteConfirmation
            user={modal.user}
            preview={modal.preview}
            onConfirm={handleDeleteConfirm}
            onCancel={() => setModal({ type: 'none' })}
            isLoading={isActionLoading}
          />
        )}
      </Modal>
    </Layout>
  );
}

export default UserManagement;
